'use client';

import { PublicRounded } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, Skeleton, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';

import type { LocaleDto, LocaleListResponse } from '@/app/api/lib/locales/locale.types';
import { useApiFetch } from '@/configurations/api/useApiFetch';
import { locales, type Locale } from '@/configurations/i18n/config';
import { Link, usePathname } from '@/configurations/i18n/navigation';
import { type NavTriggerContext, trackNavigationClick } from '@/configurations/tracking/navigation';

/**
 * `editorial.locales` est éditable à la main en admin : un code activé là-bas
 * n'existe pas forcément comme locale de l'app. Le laisser passer produirait un
 * lien `/<code>/...` que le layout `[locale]` refuse par `notFound()` — un 404 au
 * lieu d'un changement de langue. On filtre donc sur la SOT `locales`, ce qui rend
 * aussi le typage de `Link locale=` exact, sans cast.
 */
function supportedLocales(items: readonly LocaleDto[]): Array<LocaleDto & { code: Locale }> {
    return items.filter((item): item is LocaleDto & { code: Locale } =>
        (locales as readonly string[]).includes(item.code),
    );
}

type LocaleSwitcherProps = {
    /**
     * Adapte la couleur de l'icône globe selon le contexte navbar :
     * `overlay` (sur hero glassmorphique) → blanc, `solid` (navbar blanche) → secondary.
     */
    tone?: 'overlay' | 'solid';
    triggerContext: NavTriggerContext;
};

/**
 * Changer de langue = **naviguer** vers la même page dans l'autre locale.
 *
 * Chaque entrée est un vrai `<a href="/de/...">` : l'URL est partageable et le
 * cookie `NEXT_LOCALE` est réaligné au clic par next-intl. Aucune Server Action —
 * elles sont proscrites par l'ADR Front, et poser un cookie ne suffirait plus de
 * toute façon : depuis le passage de la locale dans l'URL, c'est l'URL qui décide.
 *
 * ⚠️ Ces liens ne sont PAS crawlables : le menu MUI ne se monte qu'à l'ouverture
 * et la liste des langues vient d'un fetch client. La découverte des versions
 * traduites repose donc sur les `hreflang` des métadonnées, pas sur ce composant.
 *
 * `usePathname` vient de next-intl : il renvoie le chemin SANS préfixe, ce qui
 * permet de reconstruire la même page dans une autre langue. Les search params
 * sont réinjectés — sans eux, changer de langue sur un listing filtré
 * réinitialiserait silencieusement les filtres et la pagination.
 */
export function LocaleSwitcher({ tone = 'solid', triggerContext }: LocaleSwitcherProps) {
    const currentLocale = useLocale();
    const t = useTranslations('nav');
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const apiFetch = useApiFetch();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const { data, isPending: isLoading } = useQuery({
        queryKey: ['locales'],
        queryFn: async (): Promise<LocaleDto[]> => {
            const res = await apiFetch('/api/locales');
            if (!res.ok) throw new Error(`${res.status}`);
            return ((await res.json()) as { data: LocaleListResponse }).data.items;
        },
        staleTime: 5 * 60 * 1000,
    });

    const items = supportedLocales(data ?? []);
    const current = items.find((item) => item.code === currentLocale);
    // `useSearchParams()` peut renvoyer `null` (rendu hors contexte router) : sans
    // cette garde, la navbar entière casse au lieu de perdre les search params.
    const query = Object.fromEntries(searchParams?.entries() ?? []);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        trackNavigationClick({ navItem: 'locale_switcher', triggerContext });
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    if (isLoading && !current) {
        return <Skeleton variant="circular" width={24} height={24} sx={{ mx: 1 }} />;
    }

    const iconColor = tone === 'overlay' ? 'common.white' : 'text.secondary';

    return (
        <>
            <IconButton
                onClick={handleOpen}
                aria-label={t('localeSwitcher.label')}
                aria-haspopup="menu"
                aria-expanded={open}
                sx={{ p: 0.75, color: iconColor, transition: 'color 200ms ease' }}
            >
                <PublicRounded sx={{ fontSize: 22 }} />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {items.map((item) => (
                    <MenuItem
                        key={item.code}
                        component={Link}
                        href={{ pathname, query }}
                        locale={item.code}
                        selected={item.code === currentLocale}
                        // MUI ne traduit `selected` qu'en classe CSS sur un
                        // `role="menuitem"` : sans `aria-current`, la langue active
                        // n'est signalée que visuellement.
                        aria-current={item.code === currentLocale}
                        onClick={handleClose}
                    >
                        <Typography>{item.label}</Typography>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
